import { gql, useLazyQuery } from '@apollo/client'
import { Modal } from '@farol-ds/react'
import type { SendMetrics } from '@jusbrasil-web/shared-utils-events'
import { useEffect } from 'react'

import {
  ValidateOabModalRootQuery,
  ValidateOabModalRootQueryVariables,
} from './__generated__/validate-oab-modal-with-provider.graphql'
import { ValidateOabModalContextInitialized, ValidateOabModalProvider } from './context'
import { useOabTransferringEmailJwt } from './hooks'
import { StepRenderer } from './steps'
import styles from './validate-oab-modal-with-provider.module.scss'

export const ValidateOabModalRoot = gql`
  query ValidateOabModalRoot($jwt: String) {
    root {
      viewer: me {
        ...ValidateOabModalProvider_viewer
      }

      oabTransferringDecrypt(jwt: $jwt) @ignoreExceptions {
        ...ValidateOabModalProvider_oabTransferring
      }

      ...StepRenderer_root
    }
  }

  ${ValidateOabModalProvider.fragments.viewer}
  ${ValidateOabModalProvider.fragments.oabTransferring}
  ${StepRenderer.fragments.root}
`

export interface ValidateOabModalWithProviderProps {
  setContext(context: ValidateOabModalContextInitialized): void
  origin?: string
  sendMetrics: SendMetrics
}

export function ValidateOabModalWithProvider(props: ValidateOabModalWithProviderProps) {
  const { setContext, origin, sendMetrics } = props

  const [loadModalData, { data, called, loading }] = useLazyQuery<
    ValidateOabModalRootQuery,
    ValidateOabModalRootQueryVariables
  >(ValidateOabModalRoot)

  const { emailJwt } = useOabTransferringEmailJwt()

  useEffect(() => {
    loadModalData({ variables: { jwt: emailJwt } })
  }, [emailJwt, loadModalData])

  if (loading || !called) {
    // TODO: should we show a loading modal? @felipemsantana
    return null
  }

  if (!data || !data.root.viewer) {
    // TODO: handle error case. @felipemsantana
    return null
  }

  return (
    <ValidateOabModalProvider
      setContext={setContext}
      oabTransferring={data.root.oabTransferringDecrypt}
      viewer={data.root.viewer}
      origin={origin}
      sendMetrics={sendMetrics}
    >
      {({ open, setOpen }) => (
        <Modal size="sm" open={open} onOpenChange={setOpen}>
          <Modal.Content containerClassName={styles.modalContent}>
            <StepRenderer {...data} />
          </Modal.Content>
        </Modal>
      )}
    </ValidateOabModalProvider>
  )
}
